/**
 * Surface Actions Menu
 *
 * Three-dot menu in the surface header. Hosts Copy spec, Move to… (submenu),
 * and Dismiss. The "New canvas…" submenu item opens a small dialog to
 * collect a title and layout, then triggers a move.
 */

import { cleanDisplayText } from '@/utils/display-text';
import { useMemo, useState } from 'react';
import { MoreVertical, Move, Copy, X, Plus, ArrowUp, ArrowDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuSeparator,
} from '../ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Input } from '../ui/input';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '../ui/select';
import { Button } from '../ui/button';
import { useCanvases } from '../../contexts/CanvasContext';
import { useToast } from '../ui/Toast';
import { compareSurfaces } from '../../types/canvases';
import type { Surface, CanvasLayout } from '../../types/canvases';

interface SurfaceActionsMenuProps {
  surface: Surface;
  onDismiss?: () => void;
}

export function SurfaceActionsMenu({ surface, onDismiss }: SurfaceActionsMenuProps) {
  const { canvases, surfaces, moveSurfaceToCanvas, moveSurface } = useCanvases();
  const toast = useToast();
  const [newDialogOpen, setNewDialogOpen] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newLayout, setNewLayout] = useState<CanvasLayout>('stack');

  const otherCanvases = useMemo(
    () => [...canvases.values()].filter((w) => w.canvasId !== surface.canvasId),
    [canvases, surface.canvasId],
  );

  const orderedSiblings = useMemo(
    () =>
      [...surfaces.values()]
        .filter((s) => s.canvasId === surface.canvasId)
        .sort(compareSurfaces),
    [surfaces, surface.canvasId],
  );
  const siblingIdx = orderedSiblings.findIndex((s) => s.surfaceId === surface.surfaceId);
  const isFirst = siblingIdx <= 0;
  const isLast = siblingIdx === -1 || siblingIdx >= orderedSiblings.length - 1;
  const showMoveItems = orderedSiblings.length > 1;

  const copySpec = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(surface.spec, null, 2));
      toast.success('Surface spec copied');
    } catch {
      toast.error('Copy failed');
    }
  };

  const handleMoveToExisting = (targetCanvasId: string) => {
    void moveSurfaceToCanvas(surface.canvasId, surface.surfaceId, {
      kind: 'existing',
      canvasId: targetCanvasId,
    });
  };

  const handleMoveToNew = () => {
    const title = newTitle.trim();
    if (!title) return;
    void moveSurfaceToCanvas(surface.canvasId, surface.surfaceId, {
      kind: 'new',
      title,
      layout: newLayout,
    });
    setNewDialogOpen(false);
    setNewTitle('');
    setNewLayout('stack');
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label="Surface actions"
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground focus-ring"
          >
            <MoreVertical className="h-4 w-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {showMoveItems && (
            <>
              <DropdownMenuItem
                onSelect={() => moveSurface(surface.canvasId, surface.surfaceId, 'up')}
                disabled={isFirst}
              >
                <ArrowUp />
                Move up
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() => moveSurface(surface.canvasId, surface.surfaceId, 'down')}
                disabled={isLast}
              >
                <ArrowDown />
                Move down
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )}
          <DropdownMenuItem onSelect={copySpec}>
            <Copy />
            Copy spec
          </DropdownMenuItem>
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <Move />
              Move to…
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              {otherCanvases.map((cv) => (
                <DropdownMenuItem
                  key={cv.canvasId}
                  onSelect={() => handleMoveToExisting(cv.canvasId)}
                >
                  {cleanDisplayText(cv.title) || 'Canvas'}
                </DropdownMenuItem>
              ))}
              {otherCanvases.length > 0 && <DropdownMenuSeparator />}
              <DropdownMenuItem onSelect={() => setNewDialogOpen(true)}>
                <Plus />
                New canvas…
              </DropdownMenuItem>
            </DropdownMenuSubContent>
          </DropdownMenuSub>
          {onDismiss && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={onDismiss}>
                <X />
                Dismiss
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={newDialogOpen} onOpenChange={setNewDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Move to New Canvas</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="new-canvas-title" className="text-sm font-medium">
                Title
              </label>
              <Input
                id="new-canvas-title"
                autoFocus
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleMoveToNew();
                  }
                }}
                placeholder="Canvas name"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium">Layout</label>
              <Select
                value={newLayout}
                onValueChange={(v) => setNewLayout(v as CanvasLayout)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="stack">Stack</SelectItem>
                  <SelectItem value="grid">Grid</SelectItem>
                  <SelectItem value="tabs">Tabs</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setNewDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleMoveToNew} disabled={!newTitle.trim()}>
              Move
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
